// custom-ui/ContentEntityRow.tsx
// The ONE list-row for the employer's "content" surfaces (Rekrytering / Moduler).
// A single frame — icon + title + badge + subtitle + entity-specific meta slot +
// a right-side action cluster (Edit / Preview / Delete) — so both entities look
// and behave identically. The whole row is clickable (→ detail); the action
// buttons stop propagation. Entity-specific chip rows go in `children`.
//
// NOTE: candidate-facing job cards still use `needs/NeedCard` — this row is for
// the employer authoring lists only.
import React from 'react';
import { LucideIcon } from 'lucide-react';
import { IconChip } from './IconChip';
import { EntityActionMenu } from './EntityActionMenu';
import { NEXILINK_ROW } from '@/lib/nexilink-card-styles';
import { cn } from '@/lib/utils';

export interface RowAction {
    // Any icon-shaped component: a lucide icon OR a brand mark like LinkAvatar.
    icon: React.ComponentType<{ className?: string }>;
    /** Tooltip + aria-label (e.g. "Redigera", "Förhandsgranska", "Radera"). */
    label: string;
    onClick: () => void;
    tone?: 'default' | 'danger';
    disabled?: boolean;
    /** Small uppercase pill before the label (e.g. "dev"). */
    badge?: string;
    /** Secondary line under the label explaining the action. */
    description?: string;
}

interface ContentEntityRowProps {
    icon: LucideIcon;
    iconTone?: React.ComponentProps<typeof IconChip>['tone'];
    /** Optional custom leading element (e.g. an OrgLogo). Overrides `icon` when set. */
    leading?: React.ReactNode;
    title: string;
    badge?: React.ReactNode;        // status badge (needs) — sits next to the title
    subtitle?: React.ReactNode;     // e.g. "Enhet Väst · Acme AB" or the course description
    children?: React.ReactNode;     // entity-specific meta (chip rows, dates, sources)
    onOpen?: () => void;            // whole-row click → detail
    actions?: RowAction[];          // right-side cluster
}

export const ContentEntityRow: React.FC<ContentEntityRowProps> = ({
    icon,
    iconTone,
    leading,
    title,
    badge,
    subtitle,
    children,
    onOpen,
    actions,
}) => {
    const clickable = !!onOpen;
    return (
        <div
            className={cn(NEXILINK_ROW, 'items-start', clickable && 'cursor-pointer')}
            onClick={onOpen}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={
                clickable
                    ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onOpen!();
                          }
                      }
                    : undefined
            }
        >
            {leading ? (
                <span className="self-start mt-0.5">{leading}</span>
            ) : (
                <IconChip icon={icon} tone={iconTone} className="self-start mt-0.5" />
            )}

            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">{title}</h3>
                    {badge}
                </div>
                {subtitle && <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>}
                {children}
            </div>

            {actions && actions.length > 0 && (
                <div
                    className="flex-shrink-0 self-start"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Row actions collapsed under the same "⋮" kebab used on the
                        detail pages (EntityActionMenu), so the row stays clean and
                        the affordance is consistent across the app. */}
                    <EntityActionMenu
                        actions={actions.map((a) => ({
                            label: a.label,
                            icon: a.icon,
                            onClick: a.onClick,
                            disabled: a.disabled,
                            destructive: a.tone === 'danger',
                            badge: a.badge,
                            description: a.description,
                        }))}
                    />
                </div>
            )}
        </div>
    );
};

export default ContentEntityRow;
